import React, { PropsWithChildren } from "react";
import { CDN_URL, InitOptions } from "@userjot/core";
import Script from "next/script";

interface IUserJotProviderProps {
  /** Your UserJot Project ID */
  projectId: string;
  /** The URL for the UserJot SDK. Defaults to the UserJot CDN. */
  sdkUrl?: string;
  /** The options for the UserJot SDK. These are defined in `@userjot/core`. */
  options?: InitOptions;
}

/**
 * `UserJotProvider` is a React component that initializes the UserJot SDK.
 * It should wrap the root of your application or the part of your application
 * where you want to use UserJot.
 *
 * It includes the UserJot SDK script and initializes it with your Project ID
 * and any specified options.
 *
 * @param {PropsWithChildren<IUserJotProviderProps>} props - The props for the component.
 * @param {string} props.projectId - Your UserJot Project ID. This is required.
 * @param {React.ReactNode} props.children - The child components to render.
 * @param {string} [props.sdkUrl] - The URL for the UserJot SDK. Defaults to the UserJot CDN.
 * @param {InitOptions} [props.options] - The options for the UserJot SDK. These are defined in `@userjot/core`.
 * @returns {JSX.Element} - The provider component wrapping the children and UserJot SDK scripts.
 */
export const UserJotProvider: React.FC<
  PropsWithChildren<IUserJotProviderProps>
> = ({ projectId, children, sdkUrl = CDN_URL, options }) => {
  if (!projectId) {
    console.warn(
      "UserJotProvider: `projectId` prop is missing. UserJot SDK will not be configured."
    );
    return <>{children}</>;
  }

  const serializedOptions = JSON.stringify(options);
  const inlineInitializationScript = `
    const options = ${serializedOptions};
    window.$ujq = window.$ujq || [];
    window.uj = window.uj || new Proxy({}, {
      get: function(_target, prop) {
        return function(...args) {
          window.$ujq.push([String(prop), ...args]);
        };
      }
    });
    window.uj.init('${projectId}', ${serializedOptions});
  `;

  return (
    <>
      <Script
        id="userjot-init-config"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: inlineInitializationScript,
        }}
      />
      <Script id="userjot-sdk-main" src={sdkUrl} strategy="afterInteractive" />
      {children}
    </>
  );
};
